/**
 * 從影片標題中提取 hashtag 的工具函數
 */

/**
 * 從文字中提取所有 hashtag（以 # 開頭的詞）
 * @param text 要提取的文字
 * @returns 提取到的 hashtag 陣列
 */
export function extractHashtagsFromText(text: string): string[] {
  if (!text || typeof text !== 'string') {
    return [];
  }

  // 使用正則表達式匹配所有以 # 開頭的詞，支援中英文、數字、下劃線等
  const hashtagRegex = /#[^\s#]+/g;
  const matches = text.match(hashtagRegex) || [];
  
  // 去重並轉換為小寫（保持原始大小寫）
  const uniqueHashtags = [...new Set(matches)];
  
  return uniqueHashtags;
}

/**
 * 從影片標題中提取 hashtag
 * @param title 影片標題
 * @returns 提取到的 hashtag 陣列
 */
export function extractHashtagsFromTitle(title: string): string[] {
  return extractHashtagsFromText(title);
}

/**
 * 從影片描述中提取 hashtag
 * @param description 影片描述
 * @returns 提取到的 hashtag 陣列
 */
export function extractHashtagsFromDescription(description: string): string[] {
  return extractHashtagsFromText(description);
}

/**
 * 合併多個標籤來源
 * @param sources 多個標籤陣列
 * @returns 合併後的去重標籤陣列
 */
export function mergeTags(...sources: (string[] | undefined)[]): string[] {
  const allTags: string[] = [];
  
  sources.forEach(source => {
    if (Array.isArray(source)) {
      allTags.push(...source);
    }
  });
  
  // 去重並保持順序
  return [...new Set(allTags)];
}

/**
 * 從影片物件中提取所有可能的標籤
 * @param video 影片物件
 * @returns 合併後的標籤陣列
 */
export function extractAllTagsFromVideo(video: {
  title?: string;
  description?: string;
  tags?: string[];
  youtubeHashtags?: string[];
  youtubeMetadata?: {
    hashtags?: string[];
    title?: string;
    description?: string;
  };
}): string[] {
  const allTags: string[] = [];
  
  // 1. 用戶自定義標籤
  if (video.tags && Array.isArray(video.tags)) {
    allTags.push(...video.tags);
  }
  
  // 2. YouTube API 標籤
  if (video.youtubeHashtags && Array.isArray(video.youtubeHashtags)) {
    allTags.push(...video.youtubeHashtags);
  }
  
  // 3. YouTube Metadata 標籤
  if (video.youtubeMetadata?.hashtags && Array.isArray(video.youtubeMetadata.hashtags)) {
    allTags.push(...video.youtubeMetadata.hashtags);
  }
  
  // 4. 從標題中提取的 hashtag
  if (video.title) {
    allTags.push(...extractHashtagsFromTitle(video.title));
  }
  
  // 5. 從 YouTube Metadata 標題中提取的 hashtag
  if (video.youtubeMetadata?.title) {
    allTags.push(...extractHashtagsFromTitle(video.youtubeMetadata.title));
  }
  
  // 6. 從描述中提取的 hashtag
  if (video.description) {
    allTags.push(...extractHashtagsFromDescription(video.description));
  }
  
  // 7. 從 YouTube Metadata 描述中提取的 hashtag
  if (video.youtubeMetadata?.description) {
    allTags.push(...extractHashtagsFromDescription(video.youtubeMetadata.description));
  }
  
  // 去重並保持順序
  return [...new Set(allTags)];
}
